import React, { FC } from 'react';
import { Input } from '@duxton-web/ui';
import { debounce } from 'lodash';

import facetMap, { ConstraintOperator } from './facetMap';
import TestingFacetComponent from './TestingFacetComponent';
import {
  ConstraintValueItemType,
  LocationConstraintValueItemType,
} from '@/common/const';
import { GrabAreaSelector } from '@/common/GrabAreaSelector';
import InlineInput from '@/common/InlineInput/InlineInput';
import SegmentSelector from '@/common/SegmentSelector/SegmentSelector';
import {
  detectValueItemType,
  getConstraintDisplayValue,
  getConstraintNamespace,
  getConstraintPayloadValue,
} from '@/common/valueItemType';
import { Operators } from '@/lib/App';
import { Constraint, ConstraintType } from '@/lib/PlannerDTOs';
import TypeInput from '@/pages/RolloutTab/RolloutEditor/RolloutBlock/ConstraintBlocks/ConstraintBlock/ConstraintValueInput/TypeInput';

interface ChangeProps {
  value: any;
  name: 'value' | 'op' | 'target';
}

interface FacetComponentProps {
  mode?: 'multiple' | 'tags';
  disabled?: boolean;
  constraint: Constraint;
  handleChange: (event: ChangeProps) => void;
  availableValues?: { [key: string]: string };
  facetName: string;
  testingPurpose?: boolean;
}

const FacetComponent: FC<FacetComponentProps> = ({
  disabled,
  mode,
  constraint,
  handleChange,
  availableValues,
  testingPurpose,
}) => {
  switch (constraint.type) {
    case ConstraintType.CustomString:
    case ConstraintType.CustomInt: {
      const value = constraint?.value
        ? constraint?.value?.slice(1, -1).split(',')
        : [];

      if (
        [ConstraintOperator.IN.value, ConstraintOperator.NOT_IN.value].includes(
          constraint?.op,
        )
      )
        return (
          <InlineInput
            placeholder="Comma delimited list (eg. 123, 234)"
            value={value}
            onChange={(value) => {
              handleChange({
                value: getConstraintPayloadValue({
                  constraintValues: value,
                  valueItemType: ConstraintValueItemType.INLINE.value,
                }),
                name: 'value',
              });
            }}
            status={
              constraint?.type === ConstraintType.CustomInt &&
              value?.some((val) => !Number.isInteger(Number(val)))
                ? 'error'
                : null
            }
            disabled={disabled}
            style={{
              minWidth: 300,
              minHeight: 40,
            }}
          />
        );
      else
        return (
          <Input
            defaultValue={constraint?.value}
            disabled={disabled}
            placeholder="Enter"
            status={
              constraint?.type === ConstraintType.CustomInt &&
              !Number.isInteger(Number(constraint.value))
                ? 'error'
                : null
            }
            onChange={debounce(
              (e) => handleChange({ value: e.target.value, name: 'value' }),
              200,
            )}
          />
        );
    }
    default: {
      const { valueItemType } = constraint;
      const constraintDisplayValue = getConstraintDisplayValue(constraint);
      const itemType =
        constraint?.valueItemType || detectValueItemType(constraint);
      const constraintNamespace = getConstraintNamespace(constraint);

      switch (itemType) {
        case ConstraintValueItemType.INLINE.value: {
          return (
            <InlineInput
              placeholder="Comma delimited list (eg. 123, 234)"
              value={constraintDisplayValue}
              onChange={(value) => {
                handleChange({
                  value: getConstraintPayloadValue({
                    constraintValues: value,
                    valueItemType,
                  }),
                  name: 'value',
                });
              }}
              disabled={disabled}
              style={{
                minWidth: 300,
                minHeight: 40,
              }}
            />
          );
        }
        case ConstraintValueItemType.SEGP.value: {
          return (
            <SegmentSelector
              mode={mode}
              value={constraintDisplayValue}
              namespace={constraintNamespace}
              constraint={constraint}
              onChangeSegment={(values) => {
                handleChange({
                  name: 'value',
                  value: getConstraintPayloadValue({
                    constraintValues: values,
                    valueItemType,
                    facet: constraint.target,
                  }),
                });
              }}
              wrapperStyle={{ gap: '8px' }}
              onChangeNamespace={(_value) =>
                handleChange({
                  value: null,
                  name: 'value',
                })
              }
              disabled={disabled}
            />
          );
        }
        case LocationConstraintValueItemType.GRAB_AREA.value: {
          return (
            <GrabAreaSelector
              value={constraintDisplayValue}
              onChange={(value) => {
                handleChange({
                  value: getConstraintPayloadValue({
                    constraintValues: value,
                    valueItemType,
                  }),
                  name: 'value',
                });
              }}
            />
          );
        }
        default: {
          if (testingPurpose) {
            return (
              <TestingFacetComponent
                value={constraint?.value}
                type={constraint?.target}
                onChange={(e) => {
                  handleChange({ value: e, name: 'value' });
                }}
              />
            );
          }

          const multiple = [Operators.in, Operators['not in']].includes(
            constraint.op,
          );

          const props = {
            disabled,
            value: constraint.value,
            onChange: (e) => handleChange({ value: e, name: 'value' }),
            constraint: constraint,
            availableValues: availableValues,
            facetName: constraint?.target,
            operator: constraint?.op,
            multiple,
            mode: multiple ? 'multiple' : null,
          };

          return (
            facetMap[constraint?.target]?.component?.(props) || (
              <TypeInput {...props} />
            )
          );
        }
      }
    }
  }
};

export default FacetComponent;
